import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, DollarSign, Clock, Zap, ArrowRight } from 'lucide-react';
import { dashboardService } from '../services/auth';
import { toast } from 'sonner';

// AI-TRUTH FIX: the server (POST /api/dashboard/deals) computes the new price from msrp, else price.
// Preview uses the same base so the merchant sees the price that will actually be written.
export function previewDealPrice(rec) {
  const base = Number(rec?.product_msrp ?? rec?.product_price);
  const current = Number(rec?.product_price);
  const value = Number(rec?.recommended_discount_value);
  const type = rec?.recommended_discount_type;
  if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(value) || value <= 0) return null;
  if (type === 'percentage') {
    if (value >= 100) return null;
    return { base, current: Number.isFinite(current) ? current : base, value, type, price: Math.round(base * (1 - value / 100) * 100) / 100 };
  }
  if (type === 'fixed') {
    if (value >= base) return null;
    return { base, current: Number.isFinite(current) ? current : base, value, type, price: Math.round((base - value) * 100) / 100 };
  }
  return null;   // unknown types (e.g. bogo) are never previewable, so never sent from here
}

export default function AIRecommendations() {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  // AI-TRUTH FIX (added): why the list could not load (plan gate / usage cap / error) instead of a fake empty state.
  const [loadProblem, setLoadProblem] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      const response = await dashboardService.getRecommendations();
      setLoadProblem(null);
      setRecommendations(response.recommendations || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      // AI-TRUTH FIX (replaced): every failure fell through to the empty state "Add more products", which is the wrong
      // remedy for a plan gate (403 FEATURE_NOT_AVAILABLE), a usage cap, or a server error.
      // Restoration check: only if the empty state is shown solely when the server returned an empty list.
      const d = error?.response?.data?.detail;
      if (error?.response?.status === 403 && d && typeof d === 'object' && (d.error === 'FEATURE_NOT_AVAILABLE' || d.error === 'AI_RECOMMENDATION_LIMIT_EXCEEDED')) {
        setLoadProblem({ kind: 'gated', message: d.message || 'AI recommendations are not available on your current plan.', plan: d.required_plan || null });
      } else {
        setLoadProblem({ kind: 'error' });
      }
      setRecommendations([]);
      setLoading(false);
    }
  };

  const openDealModal = (rec) => {
    setSelectedRec(rec);
    setCouponCode('');
    setShowModal(true);
  };

  const handleCreateDeal = async () => {
    if (!selectedRec) return;
    // AI-TRUTH FIX: never write without a previewable, confirmed price.
    if (!previewDealPrice(selectedRec)) return;
    
    setCreating(selectedRec.product_id);
    
    try {
      await dashboardService.createDeal({
        product_id: selectedRec.product_id,
        discount_type: selectedRec.recommended_discount_type,
        discount_value: selectedRec.recommended_discount_value,
        coupon_code: couponCode || null,
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        // AI-TRUTH FIX (added): bind the write to the exact prices shown in the confirmation; the server refuses (409) if they changed.
        expected_product_price: Number(selectedRec.product_price),
        expected_msrp: Number(selectedRec.product_msrp ?? selectedRec.product_price),
        expected_new_price: previewDealPrice(selectedRec).price
      });
      
      toast.success('Deal created from AI recommendation!', {
        // AI-TRUTH FIX (replaced): always printed "% off" even for fixed discounts and never named the new price.
        // Restoration check: only if the type is guaranteed percentage.
        // description: `${selectedRec.product_title} - ${selectedRec.recommended_discount_value}% off`
        description: `${selectedRec.product_title}: live price is now $${previewDealPrice(selectedRec).price.toFixed(2)}`
      });
      
      setRecommendations(recommendations.filter(r => r.product_id !== selectedRec.product_id));
      setShowModal(false);
      setSelectedRec(null);
      setCouponCode('');
      setCreating(null);
    } catch (error) {
      console.error('Deal creation error:', error);
      // AI-TRUTH FIX (added): a changed price is not a generic failure. Nothing was written; reload the recommendation.
      if (error.response?.status === 409 && error.response?.data?.detail?.error === 'PRICE_CHANGED') {
        toast.error('Price changed, nothing was applied', { description: 'Review the recommendation again before confirming.' });
        setShowModal(false); setSelectedRec(null); setCreating(null); setLoading(true); loadRecommendations();
        return;
      }
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to create deal';
      toast.error('Failed to create deal', {
        description: typeof errorMessage === 'string' ? errorMessage : 'Please try again or contact support'
      });
      setCreating(null);
    }
  };

  // F-M05-2: unknown score is null from the API; show neutral, never a red low-score badge.
  const getCIScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-[#9CB0C7] bg-[#9CB0C7]/10 border-[#9CB0C7]/20';
    if (score >= 800) return 'text-[#3EF1C7] bg-[#3EF1C7]/10 border-[#3EF1C7]/20';
    if (score >= 600) return 'text-[#8CF78E] bg-[#8CF78E]/10 border-[#8CF78E]/20';
    if (score >= 300) return 'text-[#FFC857] bg-[#FFC857]/10 border-[#FFC857]/20';
    return 'text-[#F55454] bg-[#F55454]/10 border-[#F55454]/20';
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#22304A] rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-[#22304A] rounded"></div>
        </div>
      </div>
    );
  }

  if (loadProblem) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 text-center" data-testid="load-problem">
        <Sparkles className="w-12 h-12 text-[#7A5CFF] mx-auto mb-4" />
        {loadProblem.kind === 'gated' ? (
          <>
            <h3 className="text-lg font-semibold text-white mb-2">Recommendations are not available right now</h3>
            <p className="text-sm text-[#9CB0C7]" data-testid="gate-message">{loadProblem.message}</p>
            {loadProblem.plan && <p className="text-xs text-[#9CB0C7] mt-2">Required plan: {String(loadProblem.plan)}</p>}
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-white mb-2">Could not load recommendations</h3>
            <p className="text-sm text-[#9CB0C7] mb-4">Something went wrong. Nothing was changed.</p>
            <button onClick={() => { setLoading(true); loadRecommendations(); }} className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white">Try again</button>
          </>
        )}
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 text-center">
        <Sparkles className="w-12 h-12 text-[#7A5CFF] mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Recommendations Yet</h3>
        <p className="text-sm text-[#9CB0C7]">Add more products to get AI-powered deal suggestions</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
      <div className="p-6 border-b border-[#22304A] bg-gradient-to-r from-[#7A5CFF]/20 to-[#3EF1C7]/10">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-[#7A5CFF]" />
          <h3 className="text-xl font-semibold text-white">AI Deal Recommendations</h3>
        </div>
        <p className="text-sm text-[#9CB0C7]">Intelligent suggestions to maximize your revenue</p>
      </div>

      <div className="p-6 space-y-4">
        {recommendations.slice(0, 3).map((rec) => (
          <div
            key={rec.product_id}
            className="rounded-lg border border-[#22304A] bg-[#111A2E] p-4 hover:border-[#7A5CFF]/40 transition-all"
          >
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-20 h-20 bg-[#1A2437] rounded-lg overflow-hidden">
                {rec.product_image ? (
                  <img src={rec.product_image} alt={rec.product_title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#9CB0C7]">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <button
                      onClick={() => navigate(`/dashboard/product/${rec.product_id}`)}
                      className="font-semibold text-white mb-1 hover:text-[#3EF1C7] transition-colors text-left"
                    >
                      {rec.product_title}
                    </button>
                    <p className="text-xs text-[#9CB0C7]">{rec.product_category} • ${rec.product_price}</p>
                  </div>
                  <div className="flex gap-2">
                    <div className={`px-2 py-1 rounded text-xs font-bold border ${getCIScoreColor(rec.current_ci_score)}`}>
                      {rec.current_ci_score ?? 'Unknown'}
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#9CB0C7] self-center" />
                    <div className={`px-2 py-1 rounded text-xs font-bold border ${getCIScoreColor(rec.predicted_ci_score)}`}>
                      {rec.predicted_ci_score ?? 'Unknown'}
                    </div>
                  </div>
                </div>

                <div className="mb-3 p-3 bg-[#7A5CFF]/10 border border-[#7A5CFF]/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-[#7A5CFF]" />
                    <span className="text-sm font-semibold text-white">
                      Recommend: {rec.recommended_discount_value}% OFF
                    </span>
                    <span className="px-2 py-0.5 bg-[#8CF78E]/20 text-[#8CF78E] rounded-full text-xs font-bold">
                      {rec.ci_score_boost == null ? 'CI score unknown' : `+${rec.ci_score_boost} CI Score`}
                    </span>
                  </div>
                  <p className="text-xs text-[#9CB0C7]">{rec.reasoning}</p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center p-2 bg-[#0E1426] rounded">
                    <DollarSign className="w-4 h-4 text-[#8CF78E] mx-auto mb-1" />
                    <div className="text-xs text-[#9CB0C7]">Est. Revenue</div>
                    <div className="text-sm font-bold text-white">{rec.potential_revenue == null ? 'Unavailable' : `$${rec.potential_revenue}`}</div>
                  </div>
                  <div className="text-center p-2 bg-[#0E1426] rounded">
                    <TrendingUp className="w-4 h-4 text-[#3EF1C7] mx-auto mb-1" />
                    <div className="text-xs text-[#9CB0C7]">Est. Conv.</div>
                    <div className="text-sm font-bold text-white">{rec.estimated_conversions}%</div>
                  </div>
                  <div className="text-center p-2 bg-[#0E1426] rounded">
                    <Clock className="w-4 h-4 text-[#FFC857] mx-auto mb-1" />
                    {/* AI-TRUTH FIX (replaced label): Best Day (a fixed category table, not learned) */}
                    <div className="text-xs text-[#9CB0C7]">Typical best day</div>
                    <div className="text-sm font-bold text-white">{rec.best_timing?.day}</div>
                  </div>
                </div>

                <p className="text-xs text-[#9CB0C7] mb-3">{rec.competitive_insight}</p>

                <button
                  onClick={() => openDealModal(rec)}
                  disabled={creating === rec.product_id}
                  className="w-full py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                  {creating === rec.product_id ? 'Creating...' : 'Create This Deal'}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* AI-TRUTH FIX (added): say what the numbers are */}
        <p className="text-xs text-[#9CB0C7]" data-testid="estimate-basis">
          Revenue, conversion, best day, predicted CI score and confidence come from fixed rules on each product's price, category and CI score. They are not live market data and not a forecast.
        </p>

        {recommendations.length > 3 && (
          <button className="w-full py-3 border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] rounded-lg transition-colors font-medium text-sm">
            View All {recommendations.length} Recommendations →
          </button>
        )}
      </div>

      {/* Create Deal Modal. AI-TRUTH FIX: rendered in a portal. Inside the card (backdrop-blur + overflow-hidden) the fixed
          overlay was clipped to the card, hiding the confirm button. Restoration check: inline render only if the confirm button is visible. */}
      {showModal && selectedRec && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-lg">
            <div className="p-6 border-b border-[#22304A]">
              <h3 className="text-xl font-semibold text-white">Create Deal from AI Recommendation</h3>
              <p className="text-sm text-[#9CB0C7] mt-1">{selectedRec.product_title}</p>
            </div>
            <div className="p-6 space-y-4">
              {/* Recommendation Summary */}
              <div className="p-4 bg-[#7A5CFF]/10 border border-[#7A5CFF]/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white font-semibold">AI Recommendation</span>
                  <span className="px-2 py-1 bg-[#8CF78E]/20 text-[#8CF78E] rounded-full text-xs font-bold">
                    {selectedRec.ci_score_boost == null ? 'CI score unknown' : `+${selectedRec.ci_score_boost} CI Score`}
                  </span>
                </div>
                {/* AI-TRUTH FIX (replaced): "{value}% OFF" for every type, no price shown.
                <div className="text-2xl font-bold text-white mb-1">{selectedRec.recommended_discount_value}% OFF</div> */}
                <div className="text-2xl font-bold text-white mb-1" data-testid="confirm-discount">
                  {selectedRec.recommended_discount_type === 'percentage'
                    ? `${selectedRec.recommended_discount_value}% OFF`
                    : `$${selectedRec.recommended_discount_value} OFF`}
                </div>
                <div className="text-xs text-[#9CB0C7]">Rule-based CI estimate: {selectedRec.predicted_ci_score ?? 'Unavailable (no CI score)'}</div>
              </div>

              {/* AI-TRUTH FIX (added): resolved product + exact price change, shown before any write */}
              {(() => {
                const pv = previewDealPrice(selectedRec);
                return pv ? (
                  <div className="p-4 rounded-lg border border-[#FFC857]/40 bg-[#FFC857]/10" data-testid="price-preview">
                    <div className="text-sm text-white font-semibold mb-1" data-testid="confirm-product">{selectedRec.product_title}</div>
                    <div className="text-sm text-[#9CB0C7]">
                      Price now ${pv.current.toFixed(2)} {'->'} <span className="text-white font-bold" data-testid="confirm-new-price">${pv.price.toFixed(2)}</span>
                    </div>
                    {pv.base !== pv.current && (
                      <div className="text-xs text-[#9CB0C7] mt-1" data-testid="confirm-base-note">Discount is taken from the list price ${pv.base.toFixed(2)}.</div>
                    )}
                    {pv.price > pv.current && (
                      <div className="text-xs text-[#F55454] mt-1" data-testid="confirm-raise-warning">Warning: this would raise the live price.</div>
                    )}
                    <p className="text-xs text-[#FFC857] mt-2">
                      Confirming changes this product's live price and publishes the deal to TheDeals.ai right away.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border border-[#F55454]/40 bg-[#F55454]/10 text-sm text-[#F55454]" data-testid="no-preview">
                    The new price can't be calculated for this recommendation, so it can't be applied from here.
                  </div>
                );
              })()}

              {/* Coupon Code Input */}
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">
                  Coupon Code (Optional)
                </label>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="SAVE20"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60"
                />
                <p className="text-xs text-[#9CB0C7] mt-1">Add a coupon code for customers to use</p>
              </div>

              {/* Estimated Impact */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#111A2E] rounded-lg">
                  <div className="text-xs text-[#9CB0C7] mb-1">Est. Revenue</div>
                  <div className="text-lg font-bold text-[#8CF78E]">{selectedRec.potential_revenue == null ? 'Unavailable' : `$${selectedRec.potential_revenue}`}</div>
                </div>
                <div className="p-3 bg-[#111A2E] rounded-lg">
                  <div className="text-xs text-[#9CB0C7] mb-1">Est. Conversions</div>
                  <div className="text-lg font-bold text-[#3EF1C7]">{selectedRec.estimated_conversions}%</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRec(null);
                    setCouponCode('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDeal}
                  disabled={creating || !previewDealPrice(selectedRec)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 transition-colors font-semibold disabled:opacity-50"
                >
                  {/* AI-TRUTH FIX (replaced label): {creating ? 'Creating...' : 'Create Deal'} */}
                  {creating ? 'Creating...' : (previewDealPrice(selectedRec) ? `Confirm: set price to $${previewDealPrice(selectedRec).price.toFixed(2)}` : 'Cannot apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
